import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PetsIcon from '@mui/icons-material/Pets';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import { PetType } from 'happypaws-shared/src/Pets/models/PetType';
import React, { useState } from 'react';
import PetProfile from '../../../../happypaws-shared/src/Pets/models/PetProfile';
import PetCardMenu from './PetCardMenu';

interface PetCardProps {
  pet: PetProfile;
  // onViewDetails: (id: string) => void;
  onEdit: (id: string) => void;
  onBookSitter: (id: string) => void;
  // onSetPrimary: (id: string) => void;
  // onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
}

const getTypeLabel = (type: PetType) => {
  switch (type) {
    case PetType.DOG:
      return { label: 'Dog', color: '#1976D2', bgColor: '#E3F2FD' };
    case PetType.CAT:
      return { label: 'Cat', color: '#9C27B0', bgColor: '#F3E5F5' };
    case PetType.OTHER:
      return { label: 'Other', color: '#FF6B35', bgColor: '#FFF3E0' };
  }
};

const getEnergyConfig = (level: 'high' | 'moderate' | 'low') => {
  switch (level) {
    case 'high':
      return { label: 'High Energy', color: '#FF6B35', bgColor: '#FFF3E0' };
    case 'moderate':
      return { label: 'Moderate', color: '#178B8E', bgColor: '#E0F2F2' };
    case 'low':
      return { label: 'Low Energy', color: '#9E9E9E', bgColor: '#F5F5F5' };
  }
};

const PetCard: React.FC<PetCardProps> = ({
  pet,
  // onViewDetails,
  onEdit,
  onBookSitter,
  // onSetPrimary,
  // onDuplicate,
  onDelete,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const typeConfig = getTypeLabel(pet.type ?? PetType.OTHER);

  return (
    <Card
      sx={{
        mb: 2,
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 3 },
          }}
        >
          {/* LEFT: Pet Photo */}
          <Box
            sx={{
              position: 'relative',
              flexShrink: 0,
              alignSelf: { xs: 'center', sm: 'flex-start' },
            }}
          >
            <Avatar
              src={pet.photoUrl ?? ''}
              alt={pet.petName}
              variant="rounded"
              sx={{
                width: 120,
                height: 120,
                backgroundColor: '#F5F5F5',
                border: '2px solid #E0E0E0',
              }}
            >
              <PetsIcon sx={{ fontSize: 48, color: '#BDBDBD' }} />
            </Avatar>
            {/* Pet type badge */}
            <Chip
              label={typeConfig.label}
              size="small"
              sx={{
                position: 'absolute',
                bottom: -6,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: typeConfig.bgColor,
                color: typeConfig.color,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                border: `1px solid ${typeConfig.color}`,
              }}
            />
          </Box>

          {/* RIGHT: Card Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Top row: Name + Primary badge + Menu */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 0.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {pet.petName}
                </Typography>
                {/* {pet.isPrimary && (
                  <Chip
                    icon={<StarIcon sx={{ fontSize: 14, color: '#FDB833 !important' }} />}
                    label="Primary"
                    size="small"
                    sx={{
                      backgroundColor: '#FFF8E1',
                      color: '#F57F17',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 24,
                    }}
                  />
                )} */}
              </Box>
              <IconButton
                size="small"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(23, 139, 142, 0.08)',
                  },
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Box>

            {/* Second row: Breed + Age */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {pet.breed} ·{' '}
              {typeof pet.age === 'number' ? `${pet.age} year${pet.age !== 1 ? 's' : ''}` : pet.age}
            </Typography>

            {/* Third row: Trait indicators */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
              {pet.houseTrained && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14, color: '#66BB6A !important' }} />}
                  label="House trained"
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 26,
                    borderColor: '#E0E0E0',
                  }}
                />
              )}
              {pet.friendlyWithChildren && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14, color: '#66BB6A !important' }} />}
                  label="Kid friendly"
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 26,
                    borderColor: '#E0E0E0',
                  }}
                />
              )}
              {pet.friendlyWithDogs && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14, color: '#66BB6A !important' }} />}
                  label="Dog friendly"
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 26,
                    borderColor: '#E0E0E0',
                  }}
                />
              )}
              {pet.friendlyWithCats && (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 14, color: '#66BB6A !important' }} />}
                  label="Cat friendly"
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 26,
                    borderColor: '#E0E0E0',
                  }}
                />
              )}
              {/* {pet.hasMedications && (
                <Chip
                  icon={
                    <MedicalServicesIcon
                      sx={{ fontSize: 14, color: "#FFA726 !important" }}
                    />
                  }
                  label="Medications"
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.75rem",
                    height: 26,
                    borderColor: "#FFA726",
                    color: "#FFA726",
                  }}
                />
              )} */}
              {pet.energyLevel && (
                <Chip
                  label={getEnergyConfig(pet.energyLevel).label}
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    height: 26,
                    backgroundColor: getEnergyConfig(pet.energyLevel).bgColor,
                    color: getEnergyConfig(pet.energyLevel).color,
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>

            {/* Fourth row: Booking stats */}
            {/* <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 2 }}
            >
              Used in {pet.bookingCount} booking
              {pet.bookingCount !== 1 ? "s" : ""}
            </Typography> */}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* <Button
                variant="outlined"
                size="small"
                startIcon={<VisibilityIcon />}
                //TODO: FIX TYPE TO NOT BE UNDEFINED
                onClick={() => onViewDetails(pet.id ?? '')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                }}
              >
                View Details
              </Button> */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => onEdit(pet.id ?? '')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchIcon />}
                onClick={() => onBookSitter(pet.id ?? '')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                }}
              >
                Book Sitter
              </Button>
            </Box>
          </Box>
        </Box>
      </CardContent>

      {/* Three-dot menu */}
      <PetCardMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        // onSetPrimary={() => onSetPrimary(pet.id ?? '')}
        // onDuplicate={() => onDuplicate(pet.id ?? '')}
        onDelete={() => onDelete(pet.id ?? '')}
        // isPrimary={pet.isPrimary}
      />
    </Card>
  );
};

export default PetCard;
